import { fetchAccumulatedOfferCommissionById } from 'actions/AgentHubActions'
import { useEffect } from 'react'
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks'
import { max } from 'lib/array/arrayUtils'
import { getAgentHubOfferCommissionBody, getAgentHubOfferCommissionBodyForBundledFlow } from 'agentHub/selectors/agentHubSelectors'
import { fetchAccumulatedOfferById } from 'actions/OfferActions'
import config from 'constants/config'
import { mapOfferToAgentHubCommissionPayload } from 'agentHub/api/mappers/commissionMap'
import { getCheckInDateFromURLSearchParams, getCheckOutDateFromURLSearchParams, getOccupanciesFromURLSearchParams } from 'lib/url/searchUrlUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useQueryParams from 'hooks/useQueryParams'
import { formatIsoDate } from 'lib/datetime/dateUtils'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { mapBedbankPackageToAgentHubPayload } from 'agentHub/api/mappers/agentHubMap'

function useAgentHubCommission(
  offerId: string,
  /**
   * @param offer - optional, to be used when the offer is not found by redux store (e.g. car hire and experience offers)
   */
  offer?: App.AgentHubOfferPayload,
) {
  const dispatch = useAppDispatch()

  const offerSummary = useAppSelector(state => state.offer.offerSummaries[offerId] || state.offer.bedbankOfferSummaries[offerId] || state.offer.tourV2Offers[offerId] || state.offer.offers[`cruise:${offerId}`])

  const agentHubOfferCommissionBody = useAppSelector(state => getAgentHubOfferCommissionBody(state, offerId))

  const mappedOfferParameter = offer && 'type' in offer && offer.type !== 'bedbank_hotel' ? mapOfferToAgentHubCommissionPayload(offer) : null

  const flightWithJourneyCommissionBody = useAppSelector(state => offer && 'journey' in offer ? getAgentHubOfferCommissionBodyForBundledFlow(state, offer) : null)

  const mappedAgentHubOfferCommission = agentHubOfferCommissionBody ?? mappedOfferParameter ?? flightWithJourneyCommissionBody

  const isFetchRequired = config.agentHub.isEnabled && (offerSummary || offer) && !mappedAgentHubOfferCommission

  const offerCommissionData = useAppSelector(state => state.agentHub.offersCommissionMap?.[offerId])

  const commissionPercentage = offerCommissionData?.commissionPercentage ?? 0
  const packagesCommission = offerCommissionData?.packages ?? []
  const minBaseCommission = offerCommissionData?.minBaseCommission || commissionPercentage
  const maxBaseCommission = offerCommissionData?.maxBaseCommission || commissionPercentage
  const greatestPackageCommission = max(packagesCommission, pkg => pkg.commissionPercentage)?.commissionPercentage ?? 0
  const bedbankOffer = useAppSelector(state => state.offer.bedbankOfferRates?.[offerId]) || {}

  useEffect(() => {
    if (isFetchRequired) {
      dispatch(fetchAccumulatedOfferById(offerId))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, isFetchRequired])

  const queryParams = useQueryParams()

  useEffect(() => {
    if (config.agentHub.isEnabled && mappedAgentHubOfferCommission) {
      const checkIn = getCheckInDateFromURLSearchParams(queryParams)
      const checkOut = getCheckOutDateFromURLSearchParams(queryParams)
      const occupancies = getOccupanciesFromURLSearchParams(queryParams)

      // overwrite bedbank packages since we need to fetch the rate based on the occupancy, check-in and check-out (available via window.location.search)
      if (agentHubOfferCommissionBody && agentHubOfferCommissionBody.type === 'bedbank_hotel' && bedbankOffer) {
        const rateKey = occupancies && checkIn && checkOut ? getBedbankRateKey(occupancies, formatIsoDate(checkIn.toDate()), formatIsoDate(checkOut.toDate())) : null
        const bedbankRates = rateKey ? bedbankOffer?.[rateKey] || [] : []
        const mappedBedbankRates = bedbankRates.map(mapBedbankPackageToAgentHubPayload)
        agentHubOfferCommissionBody.packages = mappedBedbankRates
      }
      dispatch(fetchAccumulatedOfferCommissionById(offerId, mappedAgentHubOfferCommission, {
        startDate: checkIn?.format(ISO_DATE_FORMAT),
        endDate: checkOut?.format(ISO_DATE_FORMAT),
        occupants: occupancies,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentHubOfferCommissionBody, queryParams.get('checkIn'), queryParams.get('checkOut'), queryParams.get('occupancies')])

  return { commissionPercentage, packagesCommission, greatestPackageCommission, minBaseCommission, maxBaseCommission }
}

export default useAgentHubCommission
